import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { IconPlus, IconCopy } from '@tabler/icons-react';
import { api, apiGlobal } from '../services/api';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useSignOut } from '../services/authService';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { PageHero, SectionContainer, Row, DangerZone, Badge, FooterLinks, categoryDotColor } from '../components/ds';
import { CreateWorkspaceDialog } from '../components/CreateWorkspaceDialog.jsx';
import { OWNED_WORKSPACE_CAP, isAtWorkspaceCap } from '../utils/workspaceCap';
import { assignLocation } from '../utils/navigation';

const errMessage = (err) => err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

// Owner export row copy (#1786): local time like the snapshot page, the same
// shapes the admin ExportsManagerCard uses.
const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const EXPORT_FAILED_COPY = 'Export failed. Please contact support.';
const NO_EXPORT = { copy: '', canExport: false, canDownload: false };

// Derive one row's export copy and controls from its /exports/latest body.
// `latest` undefined means the fetch never answered: no copy, no controls.
// The owner job carries only `queued | running | done | failed`; expiry is
// `expiresAt` against the client clock, and the rate limit arrives as
// `nextAllowedAt` (null when a new request is allowed).
const exportStatus = (latest, now = Date.now()) => {
  if (!latest || typeof latest !== 'object') return NO_EXPORT;
  const job = latest.job;
  if (!job) return { copy: '', canExport: true, canDownload: false };
  if (job.status === 'queued') {
    const when = formatWhen(latest.expectedStartUtc);
    return { copy: when ? `Export queued for ${when}` : 'Export queued', canExport: false, canDownload: false };
  }
  if (job.status === 'running') return { copy: 'Export running', canExport: false, canDownload: false };
  if (job.status === 'failed') return { copy: EXPORT_FAILED_COPY, canExport: true, canDownload: false };
  if (job.status === 'done') {
    const expires = Date.parse(job.expiresAt || '');
    if (!Number.isNaN(expires) && expires > now) {
      return { copy: `Export ready until ${formatDate(job.expiresAt)}`, canExport: !latest.nextAllowedAt, canDownload: true };
    }
    if (latest.nextAllowedAt) {
      return { copy: `Next export available ${formatWhen(latest.nextAllowedAt)}`, canExport: false, canDownload: false };
    }
    return { copy: 'Export expired', canExport: true, canDownload: false };
  }
  return NO_EXPORT;
};

// v4 off-card section head (#1377 board, Account page): dot + 13px label +
// quiet count ABOVE the container instead of an in-card header row.
const CardHead = ({ label, count, color }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 2px 6px' }}>
      <Box
        sx={{
          width: ds.size.dot,
          height: ds.size.dot,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: `${ds.font.meta}px`,
          fontWeight: ds.font.weightMedium,
          color: color === palette.error.main ? palette.error.main : palette.text.secondary,
        }}
      >
        {label}
      </Typography>
      {count != null && (
        <Typography component="span" sx={{ fontSize: `${ds.font.meta}px`, color: palette.text.faint }}>
          {count}
        </Typography>
      )}
    </Box>
  );
};

const Account = ({ isAuthReady = false }) => {
  const session = useUserSession();
  const signOut = useSignOut();
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeGroupId, reload: reloadGroups } = useGroup();
  const { showError, showSuccess } = useToast();

  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Owner export (#1786): the /exports/latest body per owner workspace,
  // fetched with the card and after each Export or Download click. No
  // polling. `exportBusy` holds the row whose click is in flight.
  const [exportJobs, setExportJobs] = useState({});
  const [exportBusy, setExportBusy] = useState(null);

  const [transferGroup, setTransferGroup] = useState(null);
  const [transferMembers, setTransferMembers] = useState([]);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);

  // Delete (owner) or Leave (non-owner) one workspace (#1801). `removeTarget`
  // outlives `removeOpen` so the dialog copy holds still through its exit.
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // { group, mode: 'delete' | 'leave' }
  const [removing, setRemoving] = useState(false);
  const [removeBlocker, setRemoveBlocker] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteBlocker, setDeleteBlocker] = useState(null);
  // Email-OTP step-up (#1165): once a code is sent, the user enters it to confirm.
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [deleteCode, setDeleteCode] = useState('');

  const user = session.user;
  const callerUserId = user?.userId || null;
  const callerEmail = user?.email || '';

  const refreshExport = useCallback(async (groupId) => {
    const res = await api.get(groupId, '/exports/latest');
    if (res && typeof res === 'object') setExportJobs(prev => ({ ...prev, [groupId]: res }));
  }, []);

  // Owner rows only, never the guide sandbox; a rejected fetch leaves that
  // row silent (allSettled never throws).
  const refreshExports = useCallback((list) => {
    const owned = list
      .filter(g => g.membership?.role === 'owner' && !g.guideSandbox)
      .slice(0, OWNED_WORKSPACE_CAP);
    return Promise.allSettled(owned.map(g => refreshExport(g.groupId)));
  }, [refreshExport]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGlobal.get('/groups');
      const list = res?.groups || [];
      setGroups(list);
      await refreshExports(list);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [refreshExports]);

  // POST /exports then refetch; 409 (in progress) and 429 (rate limited)
  // also refetch so the row shows why. Success toasts are console-only, so
  // the row status is the confirmation.
  const runExport = async (group) => {
    setExportBusy(group.groupId);
    try {
      let refetch = true;
      try {
        await api.post(group.groupId, '/exports');
      } catch (err) {
        showError(errMessage(err));
        refetch = err?.status === 409 || err?.status === 429;
      }
      if (refetch) await refreshExport(group.groupId).catch(() => {});
    } finally {
      setExportBusy(null);
    }
  };

  // Each click presigns a fresh link; the browser saves it as an attachment.
  // A 410 means the file aged out: refetch so the row shows the expired copy.
  const runDownload = async (group) => {
    const jobId = exportJobs[group.groupId]?.job?.jobId;
    if (!jobId) return;
    setExportBusy(group.groupId);
    try {
      const res = await api.get(group.groupId, `/exports/${encodeURIComponent(jobId)}/url`);
      if (res?.url) assignLocation(res.url);
    } catch (err) {
      showError(errMessage(err));
      if (err?.status === 410) await refreshExport(group.groupId).catch(() => {});
    } finally {
      setExportBusy(null);
    }
  };

  useEffect(() => {
    if (isAuthReady && user) load();
  }, [isAuthReady, user, load]);

  const openTransfer = async (group) => {
    setTransferGroup(group);
    setTransferTargetId('');
    try {
      const res = await api.get(group.groupId, '/members');
      const others = (res?.members || []).filter(m => m.userId !== callerUserId);
      setTransferMembers(others);
    } catch (err) {
      showError(errMessage(err));
      setTransferGroup(null);
    }
  };

  const runTransfer = async () => {
    if (!transferGroup || !transferTargetId) return;
    setTransferring(true);
    try {
      await api.put(transferGroup.groupId, `/members/${encodeURIComponent(transferTargetId)}`, { role: 'owner' });
      await api.put(transferGroup.groupId, `/members/${encodeURIComponent(callerUserId)}`, { role: 'editor' });
      showSuccess('Ownership transferred');
      setTransferGroup(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setTransferring(false);
    }
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteTyped('');
    setDeleteBlocker(null);
    setCodeSent(false);
    setDeleteCode('');
  };

  // Step 1: typed-email matches -> email a one-time code (#1165).
  const sendDeleteCode = async () => {
    if (deleteTyped.trim().toLowerCase() !== callerEmail.toLowerCase()) {
      showError('Typed email must match your account email');
      return;
    }
    setSendingCode(true);
    setDeleteBlocker(null);
    try {
      await apiGlobal.post('/profile/delete-challenge');
      setCodeSent(true);
      showSuccess(`Verification code sent to ${callerEmail}`);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setSendingCode(false);
    }
  };

  // Step 2: submit the emailed code to confirm deletion.
  const runDelete = async () => {
    if (!deleteCode.trim()) {
      showError('Enter the code from your email');
      return;
    }
    setDeleting(true);
    setDeleteBlocker(null);
    try {
      await apiGlobal.delete('/profile', { deleteCode: deleteCode.trim() });
      try { window.localStorage.removeItem('budgetrecon.activeGroupId'); } catch {}
      showSuccess('Account deleted');
      await signOut();
    } catch (err) {
      if (err.status === 403) {
        showError('That code is wrong or expired. Request a new one.');
      } else if (err.status === 409) {
        setDeleteBlocker(err.body?.message || err.body?.error || 'Cannot delete: transfer ownership first');
      } else {
        showError(errMessage(err));
      }
    } finally {
      setDeleting(false);
    }
  };

  const openRemove = (group, mode) => {
    setRemoveTarget({ group, mode });
    setRemoveBlocker(null);
    setRemoveOpen(true);
  };

  const closeRemove = () => {
    setRemoveOpen(false);
    setRemoveBlocker(null);
  };

  // DELETE /groups/{id} (owner) or POST /groups/{id}/leave (any member). Both
  // handlers answer 409 when the caller is not allowed to go: delete while
  // other members remain, leave as the last owner. Success reloads this card
  // and GroupContext; if the removed workspace was the active one, the
  // context's reload falls back to the next workspace on its own.
  const runRemove = async () => {
    if (!removeTarget) return;
    const { group, mode } = removeTarget;
    setRemoving(true);
    setRemoveBlocker(null);
    try {
      if (mode === 'delete') {
        await api.delete(group.groupId, '');
      } else {
        await api.post(group.groupId, '/leave');
      }
      showSuccess(mode === 'delete' ? 'Workspace deleted' : 'Left workspace');
      setRemoveOpen(false);
      await Promise.all([load(), reloadGroups()]);
    } catch (err) {
      if (err.status === 409) {
        setRemoveBlocker(mode === 'delete' ? 'Remove other members first' : errMessage(err));
      } else {
        showError(errMessage(err));
      }
    } finally {
      setRemoving(false);
    }
  };

  if (!isAuthReady || !user) return <CenteredLoader message="Loading account..." />;

  const ownedHouseholdGroups = (groups || []).filter(g =>
    g.type === 'household' && g.membership?.role === 'owner'
  );
  const groupList = groups || [];
  const atWorkspaceCap = isAtWorkspaceCap(groupList);

  return (
    <Box sx={{ pb: 8 }}>
      {/* v4 hero (#1377 board): "Account" title + quiet identity aux right. */}
      <PageHero
        label="Account"
        aux={
          <Box sx={{ textAlign: 'right' }}>
            {user.displayName && (
              <Typography component="div" sx={{ fontSize: '14px', color: palette.text.secondary }}>
                {user.displayName}
              </Typography>
            )}
            <Typography component="div" sx={{ fontSize: '11px', color: palette.text.dim, mt: '2px' }}>
              {user.email || ''}
            </Typography>
          </Box>
        }
      />

      {/* v4 board Account layout: off-card section heads; wide = Profile +
          Danger (left 1.2fr) beside Workspaces (right); mobile stacks
          Profile -> Workspaces -> Danger (danger always last). */}
      <Box
        sx={{
          mt: { xs: '12px', md: '14px' },
          display: 'grid',
          gap: '16px',
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' },
          gridTemplateAreas: {
            xs: '"profile" "workspaces" "danger"',
            md: '"profile workspaces" "danger workspaces"',
          },
        }}
      >
        <Box sx={{ gridArea: 'profile' }}>
          <CardHead label="Profile" color={categoryDotColor(theme, 'neutral')} />
          <SectionContainer>
            {user.email && <Row name="Email" value={user.email} />}
            {user.displayName && <Row name="Name" value={user.displayName} />}
            <Row
              name="User ID"
              value={
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '11px',
                    color: palette.text.dim,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {user.userId}
                  <Tooltip title="Copy user ID">
                    <IconButton
                      size="small"
                      aria-label="Copy user ID"
                      sx={{ padding: '2px', color: palette.text.dim }}
                      onClick={() => {
                        navigator.clipboard?.writeText(user.userId).then(
                          () => showSuccess('User ID copied'),
                          () => showError('Copy failed')
                        );
                      }}
                    >
                      <IconCopy size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              last
            />
          </SectionContainer>
        </Box>

        <Box sx={{ gridArea: 'workspaces' }}>
          <CardHead
            label="Workspaces"
            count={groupList.length}
            color={categoryDotColor(theme, 'interactive')}
          />
          <SectionContainer>
            {loading ? (
              <CenteredLoader message="Loading workspaces..." />
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : groupList.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>
                You are not a member of any workspaces.
              </Typography>
            ) : (
              groupList.map((g, idx) => {
                const name = g.name || g.groupId;
                const isOwner = g.membership?.role === 'owner';
                // Export status and controls are owner-only and never on the
                // guide sandbox row (#1786).
                const exp = isOwner && !g.guideSandbox ? exportStatus(exportJobs[g.groupId]) : NO_EXPORT;
                const busy = exportBusy === g.groupId;
                return (
                <Row
                  key={g.groupId}
                  name={name}
                  meta={[g.type || '-', g.membership?.role || '-', exp.copy].filter(Boolean).join(' · ')}
                  trailing={g.groupId === activeGroupId ? <Badge variant="current">current</Badge> : undefined}
                  actions={
                    // The guide sandbox (Practice Space) is single-user and hidden
                    // from the switcher - transferring, deleting, or leaving it is
                    // nonsensical (#1287). Delete is owner-only, Leave is for every
                    // other role, and a row never carries both (#1801).
                    g.guideSandbox ? undefined : (
                      <>
                        {exp.canExport && (
                          <Button size="small" aria-label={`Export ${name}`} disabled={busy} onClick={() => runExport(g)}>
                            Export
                          </Button>
                        )}
                        {exp.canDownload && (
                          <Button size="small" aria-label={`Download ${name}`} disabled={busy} onClick={() => runDownload(g)}>
                            Download
                          </Button>
                        )}
                        {g.type === 'household' && g.membership?.role === 'owner' && (
                          <Button size="small" onClick={() => openTransfer(g)}>Transfer ownership</Button>
                        )}
                        {g.membership?.role === 'owner' ? (
                          <Button
                            size="small"
                            color="error"
                            aria-label={`Delete ${g.name || g.groupId}`}
                            onClick={() => openRemove(g, 'delete')}
                          >
                            Delete
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="error"
                            aria-label={`Leave ${g.name || g.groupId}`}
                            onClick={() => openRemove(g, 'leave')}
                          >
                            Leave
                          </Button>
                        )}
                      </>
                    )
                  }
                  last={idx === groupList.length - 1}
                />
                );
              })
            )}
            {!loading && !error && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<IconPlus size={16} />}
                  onClick={() => setCreateOpen(true)}
                  disabled={atWorkspaceCap}
                >
                  New workspace
                </Button>
                {atWorkspaceCap && (
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                    Limit of {OWNED_WORKSPACE_CAP} Workspaces has been reached.
                  </Typography>
                )}
              </Box>
            )}
          </SectionContainer>
        </Box>

        <Box sx={{ gridArea: 'danger' }}>
          <CardHead label="Danger zone" color={palette.error.main} />
          <DangerZone
            title={null}
            message={ownedHouseholdGroups.length > 0
              ? `You own ${ownedHouseholdGroups.length} workspace${ownedHouseholdGroups.length === 1 ? '' : 's'}. Transfer ownership of any you share before deleting; workspaces where you are the only member are removed with your account.`
              : null}
            actionLabel="Delete account"
            onAction={() => setDeleteOpen(true)}
            fullWidthButton={isMobile}
          />
        </Box>
      </Box>

      {/* V4_ADDENDUM convention 9 complete: footer links on Account. */}
      <FooterLinks sx={{ mt: 3 }} />

      <CreateWorkspaceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      <Dialog open={!!transferGroup} onClose={() => !transferring && setTransferGroup(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Transfer ownership</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Promote another member of <strong>{transferGroup?.name}</strong> to owner. You will be demoted to editor.
          </DialogContentText>
          {transferMembers.length === 0 ? (
            <Alert severity="warning">No other members in this workspace. Invite someone first.</Alert>
          ) : (
            <Select
              fullWidth
              size="small"
              value={transferTargetId}
              displayEmpty
              onChange={(e) => setTransferTargetId(e.target.value)}
            >
              <MenuItem value="" disabled>Select new owner...</MenuItem>
              {transferMembers.map(m => (
                <MenuItem key={m.userId} value={m.userId}>
                  {m.displayName || m.email || m.userId} - {m.role}
                </MenuItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferGroup(null)} disabled={transferring}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runTransfer}
            disabled={transferring || !transferTargetId}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm before Delete / Leave (#1801). Nothing is autofocused, so
          Enter right after open lands on the dialog itself and does nothing;
          Esc only cancels. Both buttons take a deliberate click or Tab. */}
      <Dialog open={removeOpen} onClose={() => !removing && closeRemove()} maxWidth="xs" fullWidth>
        <DialogTitle>
          {removeTarget?.mode === 'delete' ? 'Delete workspace?' : 'Leave workspace?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: removeBlocker ? 2 : 0 }}>
            {removeTarget?.mode === 'delete' ? (
              <>
                Permanently delete <strong>{removeTarget?.group?.name || removeTarget?.group?.groupId}</strong> and
                everything in it. This cannot be undone.
              </>
            ) : (
              <>
                Leave <strong>{removeTarget?.group?.name || removeTarget?.group?.groupId}</strong>? You lose
                access until an owner invites you again.
              </>
            )}
          </DialogContentText>
          {removeBlocker && <Alert severity="error">{removeBlocker}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemove} disabled={removing}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runRemove} disabled={removing}>
            {removeTarget?.mode === 'delete'
              ? (removing ? 'Deleting...' : 'Delete workspace')
              : (removing ? 'Leaving...' : 'Leave workspace')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && !sendingCode && closeDelete()} maxWidth="xs" fullWidth>
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently remove your account, solo workspace, and all memberships. This cannot be undone.
          </DialogContentText>
          {deleteBlocker && <Alert severity="error" sx={{ mb: 2 }}>{deleteBlocker}</Alert>}
          {!codeSent ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Type <strong>{callerEmail}</strong> to confirm:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder={callerEmail}
                autoFocus
              />
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                We emailed a code to <strong>{callerEmail}</strong>. Enter it to delete your account:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={deleteCode}
                onChange={(e) => setDeleteCode(e.target.value)}
                placeholder="6-digit code"
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting || sendingCode}>Cancel</Button>
          {!codeSent ? (
            <Button
              color="error"
              variant="contained"
              onClick={sendDeleteCode}
              disabled={sendingCode || deleteTyped.trim().toLowerCase() !== callerEmail.toLowerCase()}
            >
              {sendingCode ? 'Sending...' : 'Send code'}
            </Button>
          ) : (
            <Button
              color="error"
              variant="contained"
              onClick={runDelete}
              disabled={deleting || !deleteCode.trim()}
            >
              {deleting ? 'Deleting...' : 'Delete account'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Account;
